import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { loadGuestResult, clearGuestResult, toScorePayload } from '../../utils/guestSession';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // 🟢 [新增] 获取当前路由状态

  // 🟢 [新增] 获取重定向地址，如果没有则默认为 /dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

// 🟢 修复后的 handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true); // 开始转圈

    try {
      await login(formData.email, formData.password);

      // 🟢 [新增] 访客试玩成绩补领：本 tab 里刚玩过一局但当时没注册（或注册后在等审批），
      // 登录成功后把那一局补进去。先清后发，重试/双击都无法重放。
      const stored = loadGuestResult();
      if (stored && stored.score > 0) {
        clearGuestResult();
        try {
          await apiService.saveGameScore(toScorePayload(stored));
        } catch (claimErr) {
          console.error('Guest score claim failed:', claimErr);
        }
      }
      
      // ✅ 登录成功！
      // 关键修改：不要在这里 setLoading(false)！
      // 让按钮一直保持 "Logging in..." 的状态，直到页面彻底跳转到 /dashboard。
      // 这样用户就不会看到页面“闪一下”恢复原状了。
      //navigate('/dashboard');
      navigate(from, { replace: true });
      
    } catch (err) {
      // ❌ 登录失败
      // 只有出错了，才把按钮恢复，让用户能重试
      setError(err.message);
      setLoading(false); 
    }
    // ❌ 删除 finally 块
    // finally {
    //   setLoading(false); 
    // }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-4">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold bg-gradient-to-r from-sky-500 to-blue-500 bg-clip-text text-transparent font-kid"
          >
            VOCAB RUN
          </motion.h1>
          <p className="text-gray-600 mt-2">Welcome back, runner!</p>
        </div>

        {/* Demo Accounts - 只在非生产环境显示 */}
        {import.meta.env.DEV && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-50 rounded-lg p-4 mb-6"
        >
          <h3 className="font-semibold text-blue-800 mb-2">Demo Accounts:</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <div><strong>Admin:</strong> admin@vocabrun.com / admin123</div>
            <div><strong>Teacher:</strong> teacher@school.com / teacher123</div>
            <div><strong>Student:</strong> student@school.com / student123</div>
          </div>
        </motion.div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
              required
            />
          </motion.div>

          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter your password"
              required
            />
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-sky-300 to-blue-400 hover:from-sky-400 hover:to-blue-500 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Logging in...
              </div>
            ) : (
              'Login to Start Running!'
            )}
          </motion.button>
        </form>

        {/* Forgot Password Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65 }}
          className="text-center mt-4"
        >
          <Link
            to="/forgot-password"
            className="text-sky-500 hover:text-sky-700 text-sm font-medium transition-colors"
          >
            Forgot your password?
          </Link>
        </motion.div>

        {/* Register Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center mt-6"
        >
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-sky-500 hover:text-sky-700 font-semibold transition-colors"
            >
              Register here
            </Link>
          </p>
        </motion.div>

        {/* Back to Home */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-4"
        >
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            ← Back to Home
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
